import React, { useEffect, useRef, useState } from 'react';
import { DataLayerId, SupplyShedDriveTime } from '../../lib/types';
import { DATA_LAYERS } from '../../lib/dataLayers';
import { isPerCrownAvailable } from '../../hooks/usePerCrownNativeWindow';
import { getProvidersForLayer } from '../../lib/dataProviders';

export type LayerPickerVariant = 'compact' | 'wide';

// Layers that only make sense inside a scenario (the scenario activates
// them programmatically and supplies the framing the layer needs to be
// useful in the panel). Hidden from the free-exploration picker; still
// rendered when scenario steps include them in `activeLayers`.
export const SCENARIO_ONLY_LAYERS: ReadonlySet<DataLayerId> = new Set([
  'resilience_priority',
]);

export const PICKABLE_LAYERS = Object.values(DATA_LAYERS).filter(
  (l) => !SCENARIO_ONLY_LAYERS.has(l.id),
);
export const PICKABLE_LAYER_IDS = PICKABLE_LAYERS.map((l) => l.id);

interface LayerPickerListboxProps {
  selectedLayer: DataLayerId | null;
  onLayerChange: (layer: DataLayerId) => void;
  // Used to flag layers whose data only exists for a specific (mill, dt)
  // tuple. Currently only the per-tree layer; the row picks up a
  // "CA · SIERRA NEVADA" pill when off-target.
  millCode: string | null;
  driveTime: SupplyShedDriveTime;
  // 'compact'  → map-corner control (narrow trigger, lime brand chrome)
  // 'wide'     → left-panel control (full-width, mint border)
  variant: LayerPickerVariant;
  // Shown in the trigger when no layer is selected (wide variant).
  placeholder?: string;
  // 'wide' variant only — disables the trigger (e.g. while a scenario
  // is running and controlling the layer set externally).
  disabled?: boolean;
}

// Shared listbox for picking a data layer. Renders one row per layer with
// a small provider mark (CTrees / USFS / NatureServe / Earthscale /
// Earthmover) so the audience can see at a glance which datasets are
// CTrees originals vs. external. The trigger style is variant-driven so
// the same component fits both the map-corner control and the left-panel
// form-style picker.
export const LayerPickerListbox: React.FC<LayerPickerListboxProps> = ({
  selectedLayer,
  onLayerChange,
  millCode,
  driveTime,
  variant,
  placeholder = 'Select a layer',
  disabled = false,
}) => {
  const layer = selectedLayer ? DATA_LAYERS[selectedLayer] : null;
  const layerIds = PICKABLE_LAYER_IDS;
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const ref = useRef<HTMLDivElement>(null);
  const showPerCrownLocalityPill = !isPerCrownAvailable(millCode, driveTime);
  const isCompact = variant === 'compact';
  const triggerProviders = layer ? getProvidersForLayer(layer.id) : null;

  const toggle = () => {
    if (disabled) return;
    const opening = !isOpen;
    setIsOpen(opening);
    setFocusedIndex(opening ? layerIds.indexOf(selectedLayer!) : -1);
  };

  const select = (id: DataLayerId) => {
    onLayerChange(id);
    setIsOpen(false);
    setFocusedIndex(-1);
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
        setFocusedIndex(layerIds.indexOf(selectedLayer!));
      }
      return;
    }
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setFocusedIndex(-1);
        break;
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((i) => Math.min(i + 1, layerIds.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((i) => Math.max(i - 1, 0));
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (focusedIndex >= 0) select(layerIds[focusedIndex]);
        break;
    }
  };

  useEffect(() => {
    if (focusedIndex >= 0 && isOpen) {
      const items = ref.current?.querySelectorAll('[role="option"]');
      (items?.[focusedIndex] as HTMLElement)?.focus();
    }
  }, [focusedIndex, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    // Skip closes when the mousedown lands in a scrollbar gutter — the
    // event's target is the scrollable element itself (not a child) and
    // the x-coordinate falls in the reserved scrollbar width. Without
    // this, dragging the left panel's outer scrollbar with the dropdown
    // open immediately collapses the dropdown.
    const isScrollbarClick = (e: MouseEvent): boolean => {
      const target = e.target as HTMLElement | null;
      if (!target) return false;
      const gutter = target.offsetWidth - target.clientWidth;
      if (gutter <= 0) return false;
      const rect = target.getBoundingClientRect();
      return e.clientX >= rect.right - gutter;
    };
    const onClick = (e: MouseEvent) => {
      if (isScrollbarClick(e)) return;
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [isOpen]);

  const renderLogo = (
    provider: { logoPath: string; needsInvert?: boolean; shortLabel: string },
    extraClass = '',
  ) => (
    <img
      src={provider.logoPath}
      alt=""
      className={`w-5 h-5 flex-shrink-0 object-contain ${extraClass}`}
      style={provider.needsInvert ? { filter: 'invert(1)' } : undefined}
    />
  );

  const Chevron = (
    <svg
      width="10"
      height="6"
      viewBox="0 0 10 6"
      fill="none"
      className={`flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
    >
      <path
        d="M1 1L5 5L9 1"
        stroke={isCompact ? '#c1f04f' : '#46ce85'}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  return (
    <div
      ref={ref}
      className={isCompact ? 'relative' : 'relative w-full'}
      onKeyDown={handleKey}
    >
      {isCompact ? (
        <button
          onClick={toggle}
          disabled={disabled}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          className="flex items-center gap-3 h-[36px] px-4 bg-figma-card rounded-l-[10px] border-r border-figma-panel/50 hover:bg-figma-panel disabled:opacity-50 disabled:cursor-not-allowed transition-smooth"
        >
          <svg width="15" height="14" viewBox="0 0 15 14" fill="none" className="flex-shrink-0">
            <path
              d="M7.5 14L0 8.85562L1.375 7.93703L7.5 12.126L13.625 7.93703L15 8.85562L7.5 14ZM7.5 10.2887L0 5.14438L7.5 0L15 5.14438L7.5 10.2887ZM7.5 8.41472L12.2917 5.14438L7.5 1.87404L2.70833 5.14438L7.5 8.41472Z"
              fill="#c1f04f"
            />
          </svg>
          <span className="font-roboto font-bold text-[14px] tracking-[1px] text-figma-lime">
            {layer?.displayName ?? 'Select Layer'}
          </span>
          {Chevron}
        </button>
      ) : (
        <button
          onClick={toggle}
          disabled={disabled}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          className="flex items-center gap-3 w-full h-[44px] px-4 bg-figma-card border border-figma-mint rounded-figma-button hover:border-figma-mint focus:outline-none focus:ring-2 focus:ring-figma-mint disabled:opacity-50 disabled:cursor-not-allowed transition-smooth"
        >
          <span className="flex-1 text-left text-body font-roboto text-figma-text truncate">
            {layer?.displayName ?? placeholder}
          </span>
          {triggerProviders && (
            <>
              {renderLogo(triggerProviders.primary)}
              {triggerProviders.collaborator && renderLogo(triggerProviders.collaborator)}
            </>
          )}
          {Chevron}
        </button>
      )}

      {isOpen && (
        <div
          role="listbox"
          aria-label="Select data layer"
          className={`absolute mt-2 bg-figma-card border border-figma-mint rounded-[10px] shadow-figma-card overflow-hidden z-[1100] ${
            isCompact ? 'top-full right-0 w-72' : 'top-full left-0 w-full'
          }`}
        >
          <div className="py-2 max-h-[60vh] overflow-y-auto">
            {PICKABLE_LAYERS.map((dataLayer, index) => {
              const providers = getProvidersForLayer(dataLayer.id);
              const isSelected = selectedLayer === dataLayer.id;
              // Section headers: render before the first row of each tier.
              // CTrees tier opens the list; Data Partners tier starts at
              // the first row whose primary provider isn't CTrees. This
              // tracks DATA_LAYERS insertion order — if you reorder the
              // map, headers stay correctly placed without a hand-coded
              // boundary index.
              const isFirstRow = index === 0;
              const prevIsCtrees =
                index > 0 &&
                getProvidersForLayer(PICKABLE_LAYERS[index - 1].id).primary.id === 'ctrees';
              const isCtrees = providers.primary.id === 'ctrees';
              const showCtreesHeader = isFirstRow && isCtrees;
              const showPartnersHeader = !isCtrees && (isFirstRow || prevIsCtrees);
              // The per-tree layer's data is published only for the
              // (california-sierra-nevada, 60 min) tuple. On any other
              // shed, picking the row lands on a redirect card. Dim the
              // row to ~60 % opacity + show a hover-tooltip explaining
              // the locality so users see the gating before they click.
              const isPerCrownGated =
                dataLayer.id === 'tree_crown_segmentation' && showPerCrownLocalityPill;
              return (
                <React.Fragment key={dataLayer.id}>
                  {showCtreesHeader && <SectionHeader label="CTrees" />}
                  {showPartnersHeader && <SectionHeader label="Data partners" />}
                  <button
                    role="option"
                    aria-selected={isSelected}
                    tabIndex={focusedIndex === index ? 0 : -1}
                    onClick={() => select(dataLayer.id)}
                    title={
                      isPerCrownGated
                        ? 'Available in California Sierra Nevada · 60-min shed only'
                        : undefined
                    }
                    className={`w-full px-4 py-2.5 text-left font-roboto transition-smooth outline-none focus:bg-figma-lime/10 ${
                      isSelected ? 'bg-figma-lime/20' : 'hover:bg-figma-panel/50'
                    } ${isPerCrownGated ? 'opacity-60' : ''}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`text-[14px] ${
                          isSelected ? 'text-figma-lime font-bold' : 'text-figma-text'
                        }`}
                      >
                        {dataLayer.displayName}
                      </span>
                      {renderLogo(providers.primary)}
                      {providers.collaborator && renderLogo(providers.collaborator)}
                      {dataLayer.id === 'tree_crown_segmentation' && showPerCrownLocalityPill && (
                        <span
                          className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold leading-none bg-figma-mint/20 text-figma-mint tracking-wider"
                          title="Per-tree data is currently published for the California Sierra Nevada 60-min supply shed only"
                        >
                          DEMO · CA · SIERRA NEVADA
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 text-[11px] text-neutral-500 font-roboto">
                      <span
                        className={
                          providers.primary.isOriginal
                            ? 'text-figma-lime/80 font-medium'
                            : 'text-neutral-400'
                        }
                      >
                        {providers.primary.shortLabel}
                      </span>
                      {providers.collaborator && (
                        <>
                          <span className="mx-1">·</span>
                          <span className="text-neutral-400">
                            via {providers.collaborator.shortLabel}
                          </span>
                        </>
                      )}
                      {(dataLayer.resolution || dataLayer.years) && (
                        <>
                          <span className="mx-1">·</span>
                          <span>
                            {[dataLayer.resolution, dataLayer.years]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                        </>
                      )}
                    </div>
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Tier label rendered between groups of rows. Aria-hidden because each
// option already exposes its provider via the row's primary mark; the
// header is purely a visual divider, not a navigation target.
const SectionHeader: React.FC<{ label: string }> = ({ label }) => (
  <div
    aria-hidden="true"
    className="px-4 pt-3 pb-1 text-[10px] font-roboto-mono uppercase tracking-[0.14em] text-figma-ink-mute"
  >
    {label}
  </div>
);
